import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject, BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class GlobalFilterService {
  private filters: any = {
    queryParams: {},
    requestParams: {},
  };
  constructor(private http: HttpClient) { }
  private widgetId: string | null = null;
  private filterSubject = new Subject<any>();
  private currentSelectedFilter = new Subject<any>();
  private selectedFilters: { [key: string]: string | null } = {};
  private widgetRefreshSubject = new Subject<string>();
  private resetStateSubject = new Subject<void>();
  private tableDataSubject = new BehaviorSubject<any[]>([]);
  private widgetDataSubject = new BehaviorSubject<Record<string, any[]>>({});
  private statsSubject = new BehaviorSubject<any>({});
  private chartDataSubject = new BehaviorSubject<any[]>([]);
  chartData$ = this.chartDataSubject.asObservable();
  // inside GlobalFilterService
  private workflowCtxSubject = new BehaviorSubject<Record<string, any>>({});
  workflowCtx$ = this.workflowCtxSubject.asObservable();

  setWorkflowCtx(partial: Record<string, any>) {
    this.workflowCtxSubject.next({ ...this.workflowCtxSubject.value, ...partial });
  }

  getWorkflowCtx<T = any>(): T {
    return this.workflowCtxSubject.value as T;
  }

  clearWorkflowCtx() {
    this.workflowCtxSubject.next({});
  }


  filter$ = this.filterSubject.asObservable();
  currentFilter$ = this.currentSelectedFilter.asObservable();
  widgetRefresh$ = this.widgetRefreshSubject.asObservable();
  resetState$ = this.resetStateSubject.asObservable();
  tableData$ = this.tableDataSubject.asObservable();
  widgetData$ = this.widgetDataSubject.asObservable();
  stats$ = this.statsSubject.asObservable();
  activeFilter!: any;
  dropdownOptions!: { [key: string]: string[] };

  isSelected(key: string, value: string): boolean {
    return this.selectedFilters[key] === value;
  }
  updateFilterSelection(
    key: string,
    values: any[],
    isQueryParam = false,
    widgetId?: string,
    emit: boolean = true
  ): void {
    // console.log('[GlobalFilterService] updateFilterSelection called with:', { key, values, isQueryParam });
    this.selectedFilters[key] = values?.[0] || null;

    if (isQueryParam) {
      this.filters.queryParams[key] = values;
    } else {
      this.filters.requestParams[key] = values;
      delete this.filters.requestParams['addButton'];
    }
    this.widgetId = widgetId || null;
    if (emit) {
      const filterData = {
        ...this.filters,
        widgetId: this.widgetId,
        changedFilterKey: key
      };
      // console.log('[GlobalFilterService] Emitting filter data:', filterData);
      this.filterSubject.next(filterData);
      this.currentSelectedFilter.next({ key });
    }
  }

  requestWidgetRefresh(widgetId: string): void {
    this.widgetRefreshSubject.next(widgetId);
  }

  resetFilterSelection(key: string, isQueryParam = false): void {
    delete this.selectedFilters[key];
    if (isQueryParam) {
      delete this.filters.queryParams[key];
    } else {
      delete this.filters.requestParams[key];
    }
    this.filterSubject.next({
      ...this.filters,
      widgetId: this.widgetId,
    });
  }

  deleteKey(key: string, isQueryParam = false): void {
    delete this.selectedFilters[key];
    if (isQueryParam) {
      delete this.filters.queryParams[key];
    } else {
      delete this.filters.requestParams[key];
    }
  }

  getCurrentFilters(): any {
    return this.filters;
  }
  updateAllFilters(newFilters: any): void {
    this.filters = newFilters;
    this.filterSubject.next(this.filters);
    this.currentSelectedFilter.next({});
  }

  clearAllFilters() {
    this.filters = { queryParams: {}, requestParams: {} };
    this.widgetId = null;
    this.filterSubject.next({ queryParams: {}, requestParams: {}, widgetId: null, changedFilterKey: null });
    this.selectedFilters = {};
    this.currentSelectedFilter.next('');
  }

  initialTrigger() {
    this.filterSubject.next({ queryParams: {}, requestParams: {}, widgetId: null });
  }

  setTableData(data: any[]) {
    this.tableDataSubject.next(data);

  }

  setChartData(data: any[]) {
    this.chartDataSubject.next(data);
  }

  getChartData(): any[] {
    return this.chartDataSubject.value;
  }

  getTableData(): any[] {
    return this.tableDataSubject.value;
  }

  setWidgetData(widgetId: string, data: any[]): void {
    if (!widgetId) {
      return;
    }

    const current = this.widgetDataSubject.value || {};
    this.widgetDataSubject.next({
      ...current,
      [widgetId]: Array.isArray(data) ? data : []
    });
  }

  getWidgetData(widgetId: string): any[] {
    if (!widgetId) {
      return [];
    }

    const current = this.widgetDataSubject.value || {};
    const rows = current[widgetId];
    return Array.isArray(rows) ? rows : [];
  }

  clearWidgetData(widgetId?: string): void {
    if (widgetId) {
      const current = { ...(this.widgetDataSubject.value || {}) };
      delete current[widgetId];
      this.widgetDataSubject.next(current);
      return;
    }

    this.widgetDataSubject.next({});
  }

  resetDashboardState(): void {
    this.clearAllFilters();
    this.activeFilter = null;
    this.setTableData([]);
    this.clearWidgetData();
    this.clearWorkflowCtx();
    this.statsSubject.next({});
    this.resetStateSubject.next();
  }

  updateStat(key: string, value: any) {
    this.statsSubject.next({ ...this.statsSubject.value, [key]: value });
  }

  getStat(key: string) {
    return this.statsSubject.value[key];
  }
  downloadExcel(apiUrl: string, filterPayload: any): Observable<Blob> {
    return this.http.post(apiUrl, filterPayload, {
      responseType: 'blob',
      withCredentials: false   // 👈 STOPS interceptor from reading undefined cookie
    });
  }

}
